'use client';

import * as React from 'react';

import * as ToggleGroup from '@radix-ui/react-toggle-group';

import { cn } from '../../lib/utils';

export interface SegmentedOption {
    value: string;
    label: React.ReactNode;
}

interface SegmentedProps {
    value: string;
    onValueChange: (value: string) => void;
    options: SegmentedOption[];
    className?: string;
    'aria-label'?: string;
}

/**
 * Single-select segmented control (clean pill toggle). Used for theme,
 * script/iframe, and the ticker %/¢ display mode.
 */
export function Segmented({ value, onValueChange, options, className, ...props }: SegmentedProps) {
    return (
        <ToggleGroup.Root
            type="single"
            value={value}
            onValueChange={(next) => {
                if (next) onValueChange(next);
            }}
            className={cn(
                // kalshi.com's segmented switch: a 32px pill track, the chosen option raised on it.
                'inline-flex h-8 items-center rounded-x50 bg-container-x30 p-0.5',
                className,
            )}
            aria-label={props['aria-label']}
        >
            {options.map((option) => (
                <ToggleGroup.Item
                    key={option.value}
                    value={option.value}
                    className={cn(
                        'inline-flex h-7 items-center justify-center gap-1.5 rounded-x50 border border-solid border-transparent px-3 font-kalshi-sans text-[13px] font-medium leading-[18px] text-text-x20 transition-colors',
                        'hover:text-text-x10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-x20',
                        'data-[state=on]:border-stroke-x40 data-[state=on]:bg-surface-x10 data-[state=on]:text-text-x10',
                    )}
                >
                    {option.label}
                </ToggleGroup.Item>
            ))}
        </ToggleGroup.Root>
    );
}
